import useTitanApp from "../../../hooks/useTitanApp";
import {
  addPatientAllergies,
  getPatientAllergies,
  setCurrentShowAddAllergiessModal,
  setCurrentPatientAllergyToEdit,
} from "../../../dispatchers/patientDispatchers";
import { Modal } from "react-bootstrap";
import { IoClose } from "react-icons/io5";
import { useRef, useState } from "react";
import { charLimitMessage } from "@/lib/utils";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import CustomSelectionDropdown from "../../controls/CustomSelectionDropdown";
import { LuHeart } from "react-icons/lu";
import {
  ALLERGY_NAME_MAX_LENGTH,
  ALLERGY_TEXT_MAX_LENGTH,
} from "@/lib/allergyData";

export default function PatientAddAllergiesModal() {
  const { appState, patientState } = useTitanApp();
  const { darkMode } = appState || {};
  const {
    selectedPatientDetails,
    showAddAllergiessModal,
    addPatientAllergiesLoader,
    isAllergiesEditMode,
    selectedPatientAllergy,
    consultationId,
  } = patientState || {};
  const formikRef = useRef();
  const [buttonsDisabled, setButtonsDisabled] = useState(false);

  const closeModal = () => {
    formikRef.current?.resetForm();
    setCurrentShowAddAllergiessModal(false);
    if (isAllergiesEditMode) {
      setCurrentPatientAllergyToEdit(null);
    }
  };
  const severityOptions = [
    { value: "1", label: "Mild" },
    { value: "2", label: "Moderate" },
    { value: "3", label: "Severe" },
  ];

  const initialValues = {
    AllergyName: selectedPatientAllergy?.allergyName || "",
    Severity: selectedPatientAllergy
      ? selectedPatientAllergy?.severity?.toString() || ""
      : "",
    TypicalReaction: selectedPatientAllergy?.typicalReaction || "",
    AdditionalNotes: selectedPatientAllergy?.additionalNotes || "",
  };

  const validationSchema = Yup.object({
    AllergyName: Yup.string()
      .required("Allergy Name is required")
      .test(
        "not-only-spaces",
        "Allergy Name cannot be just spaces",
        (value) => value && value.trim().length > 0,
      )
      .matches(
        /^(?=.*[a-zA-Z])[a-zA-Z0-9\s,'()-]+$/,
        "Enter a valid Allergy Name",
      )
      .max(ALLERGY_NAME_MAX_LENGTH, `Maximum ${ALLERGY_NAME_MAX_LENGTH} characters`),
    Severity: Yup.string().required("Severity is required"),
    TypicalReaction: Yup.string()
      .nullable()
      .max(ALLERGY_TEXT_MAX_LENGTH, `Maximum ${ALLERGY_TEXT_MAX_LENGTH} characters`), // Optional
    AdditionalNotes: Yup.string()
      .nullable()
      .max(ALLERGY_TEXT_MAX_LENGTH, `Maximum ${ALLERGY_TEXT_MAX_LENGTH} characters`), // Optional
  });

  const handleAddClick = async (values) => {
    setButtonsDisabled(true);
    let lstAllergies = [];
    const allergies = {
      allergyName: values.AllergyName,
      severity: values.Severity === "" ? null : Number(values.Severity),
      typicalReaction: values.TypicalReaction,
      additionalNotes: values.AdditionalNotes,
    };

    if (isAllergiesEditMode) {
      lstAllergies.push(selectedPatientAllergy);
    }
    lstAllergies.push(allergies);
    const patientAllergies = {
      consultationId,
      patientId: selectedPatientDetails?.patientID,
      allergies: lstAllergies,
    };

    await addPatientAllergies(patientAllergies);
    await getPatientAllergies(selectedPatientDetails?.patientID);
    setCurrentPatientAllergyToEdit(null);
    closeModal();
  };

  return (
    <Modal
      show={showAddAllergiessModal}
      onHide={closeModal}
      centered
      backdrop="static"
      dialogClassName="pc-modal"
      size="md"
    >
      <div className="header">
        <div className="info pc-add-modal-header-info">
          <div>
            <LuHeart color={darkMode ? "#bdbdbd" : "#0a0a0a"} />
          </div>
          <p className="label">
            {isAllergiesEditMode ? "Edit" : "Add"} Patient Allergy
          </p>
          <p className="desc"></p>
        </div>
        <IoClose
          size={18}
          color={darkMode ? "#bdbdbd" : "#7c7c7c"}
          onClick={closeModal}
        />
      </div>

      <div className="body" id="bodyID">
        <Formik
          innerRef={formikRef}
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            handleAddClick(values);
          }}
        >
          {({ values, errors, touched, handleChange }) => (
            <Form>
              <div className="fields-wrapper">
                <div className="form-group">
                  <label>Allergy Name *</label>
                  <input
                    className="template_input text_input_field"
                    type="text"
                    name="AllergyName"
                    placeholder="e.g., Peanuts, Pollen, Pencillin"
                    value={values.AllergyName}
                    onChange={handleChange}
                    maxLength={ALLERGY_NAME_MAX_LENGTH}
                    autoComplete="off"
                    autoFocus
                  />
                  {((touched.AllergyName && errors.AllergyName) ||
                      (values.AllergyName || "").length >= ALLERGY_NAME_MAX_LENGTH) && (
                    <p className="field-error">
                      {(values.AllergyName || "").length >= ALLERGY_NAME_MAX_LENGTH
                        ? charLimitMessage(ALLERGY_NAME_MAX_LENGTH)
                        : errors.AllergyName}
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <CustomSelectionDropdown
                    label="Severity *"
                    name="Severity"
                    value={values.Severity}
                    options={severityOptions}
                    onChange={handleChange}
                  />
                  {touched.Severity && errors.Severity && (
                    <p className="field-error">{errors.Severity}</p>
                  )}
                </div>
                <div className="form-group">
                  <label>Typical Reaction (optional)</label>
                  <input
                    className="template_input text_input_field"
                    type="text"
                    value={values.TypicalReaction}
                    name="TypicalReaction"
                    placeholder="e.g., Rash, Sneezing, Swelling"
                    onChange={handleChange}
                    maxLength={ALLERGY_TEXT_MAX_LENGTH}
                    autoComplete="off"
                  />
                  {((touched.TypicalReaction && errors.TypicalReaction) ||
                      (values.TypicalReaction || "").length >= ALLERGY_TEXT_MAX_LENGTH) && (
                    <p className="field-error">
                      {(values.TypicalReaction || "").length >= ALLERGY_TEXT_MAX_LENGTH
                        ? charLimitMessage(ALLERGY_TEXT_MAX_LENGTH)
                        : errors.TypicalReaction}
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <label>Additional Notes (optional)</label>
                  <input
                    className="template_input text_input_field"
                    type="text"
                    name="AdditionalNotes"
                    value={values.AdditionalNotes}
                    onChange={handleChange}
                    placeholder="Any additional information..."
                    maxLength={ALLERGY_TEXT_MAX_LENGTH}
                    autoComplete="off"
                  />
                  {((touched.AdditionalNotes && errors.AdditionalNotes) ||
                      (values.AdditionalNotes || "").length >= ALLERGY_TEXT_MAX_LENGTH) && (
                    <p className="field-error">
                      {(values.AdditionalNotes || "").length >= ALLERGY_TEXT_MAX_LENGTH
                        ? charLimitMessage(ALLERGY_TEXT_MAX_LENGTH)
                        : errors.AdditionalNotes}
                    </p>
                  )}
                </div>
              </div>
              <div className="footer-actions">
                <button className="cancel" type="button" onClick={closeModal}>
                  Cancel
                </button>
                <button
                  className="done"
                  type="submit"
                  disabled={buttonsDisabled}
                >
                  {isAllergiesEditMode
                    ? addPatientAllergiesLoader
                      ? "Updating Allergy..."
                      : "Update Allergy"
                    : addPatientAllergiesLoader
                      ? "Adding Allergy..."
                      : "Add Allergy"}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
}
